import { CostCenter } from '@portal/portal-core';
import { FILTER_ACTION_TYPES } from 'constants/super-admin';

import type { LicenseSearchHit, LicenseSelectorInfo } from '../utils';

import {
  SUPER_ADMIN_ADD_USERS,
  SUPER_ADMIN_ARCHIVE_ACTIVE_EOT,
  SUPER_ADMIN_ARCHIVE_ACTIVE_EOT_ERROR,
  SUPER_ADMIN_ARCHIVE_ACTIVE_EOT_RESET,
  SUPER_ADMIN_ARCHIVE_ACTIVE_EOT_SUCCESS,
  SUPER_ADMIN_FETCH_ACTIVE_EOT,
  SUPER_ADMIN_FETCH_ACTIVE_EOT_ERROR,
  SUPER_ADMIN_FETCH_ACTIVE_EOT_SUCCESS,
  SUPER_ADMIN_BULK_ACTIONS,
  SUPER_ADMIN_CLEAR_USERS,
  SUPER_ADMIN_CREATE_LICENSE,
  SUPER_ADMIN_CREATE_PRODUCT_VERSION,
  SUPER_ADMIN_CREATE_PRODUCT_VERSION_SUCCESS,
  SUPER_ADMIN_FETCH_PRODUCT_FEATURES,
  SUPER_ADMIN_CONTINUE_LICENSE_SYNC,
  SUPER_ADMIN_DISMISS_LICENSE_SYNC,
  SUPER_ADMIN_LICENSE_FETCH_RESUME,
  SUPER_ADMIN_LICENSE_FETCH_FAILED,
  SUPER_ADMIN_FETCH_PRODUCT_VERSIONS,
  SUPER_ADMIN_HIDE_MODAL,
  SUPER_ADMIN_LICENSE_FETCH_SUCCESS,
  SUPER_ADMIN_PRODUCT_FEATURES_FETCH_SUCCESS,
  SUPER_ADMIN_FETCH_SEAT_FEATURES,
  SUPER_ADMIN_SEAT_FEATURES_FETCH_SUCCESS,
  SUPER_ADMIN_SET_SEAT_FEATURE,
  SUPER_ADMIN_PRODUCT_VERSIONS_FETCH_SUCCESS,
  SUPER_ADMIN_REQUEST_UN_PHANTOM_USERS,
  SUPER_ADMIN_SEARCH_COMPLETE,
  SUPER_ADMIN_SEARCH_LICENSES_SUCCESS,
  SUPER_ADMIN_SEARCH_LICENSES_FAILURE,
  SUPER_ADMIN_CLEAR_LICENSE_SEARCH,
  SUPER_ADMIN_SET_LICENSE_VIEW_MODE,
  SUPER_ADMIN_CHECK_COST_CENTER_NAME,
  SUPER_ADMIN_CHECK_COST_CENTER_NAME_SUCCESS,
  SUPER_ADMIN_CHECK_COST_CENTER_NAME_FAILURE,
  SUPER_ADMIN_SET_SELECTED_USER_CCS,
  SUPER_ADMIN_SET_USER_EMAIL_VERIFIED,
  SUPER_ADMIN_SET_USER_EMAIL_VERIFIED_ERROR,
  SUPER_ADMIN_SET_USER_EMAIL_VERIFIED_SUCCESS,
  SUPER_ADMIN_SET_USER_PHANTOM,
  SUPER_ADMIN_SET_USER_PHANTOM_ERROR,
  SUPER_ADMIN_SET_USER_PHANTOM_SUCCESS,
  SUPER_ADMIN_SET_USERS,
  SUPER_ADMIN_SHOW_CREATION_ERR,
  SUPER_ADMIN_SHOW_CREATION_SUCCESS,
  SUPER_ADMIN_SHOW_MANAGE_ERR,
  SUPER_ADMIN_SHOW_MANAGE_SUCCESS,
  SUPER_ADMIN_UN_PHANTOM_USERS_SUCCESS,
  SUPER_ADMIN_UPDATE_PRODUCT_ASSETS,
  SUPER_ADMIN_UPDATE_PRODUCT_ASSETS_SUCCESS,
  SUPER_ADMIN_UPDATE_PRODUCT_IS_ACTIVE,
  SUPER_ADMIN_UPDATE_PRODUCT_IS_ACTIVE_SUCCESS,
  SUPER_ADMIN_UPDATE_PRODUCT_WHITELIST,
  SUPER_ADMIN_UPDATE_PRODUCT_WHITELIST_SUCCESS,
  SUPER_ADMIN_UPDATE_USER_FIELD,
  SUPER_ADMIN_UPDATE_USER_FIELD_ERROR,
  SUPER_ADMIN_UPDATE_USER_FIELD_SUCCESS,
  SUPER_ADMIN_VERIFY_USERS_EMAILS,
  SUPER_ADMIN_VERIFY_USERS_SUCCESS,
} from '../types';

export const fetchSuccess = (
  data: unknown,
  requestData: unknown,
): { type: string; data: unknown; requestData: unknown } => ({
  type: SUPER_ADMIN_LICENSE_FETCH_SUCCESS,
  data,
  requestData,
});

export const fetchFailure = (
  requestData: unknown,
  error: string,
): { type: string; requestData: unknown; error: string } => ({
  type: SUPER_ADMIN_LICENSE_FETCH_FAILED,
  requestData,
  error,
});

export const continueLicenseSync = (
  ccType: string,
): { type: string; ccType: string } => ({
  type: SUPER_ADMIN_CONTINUE_LICENSE_SYNC,
  ccType,
});

export const dismissLicenseSync = (): { type: string } => ({
  type: SUPER_ADMIN_DISMISS_LICENSE_SYNC,
});

export const fetchResume = (
  requestData: unknown,
): { type: string; requestData: unknown } => ({
  type: SUPER_ADMIN_LICENSE_FETCH_RESUME,
  requestData,
});

export interface LicenseSearchResult {
  hits: LicenseSearchHit[];
  total: number;
  limit: number;
  offset: number;
}

// `requestId` is the id of the ADMIN_ELASTICSEARCH_COST_CENTERS_SEARCH request this response
// answers, so the reducer can drop responses that are no longer the latest request.
export const searchLicensesSuccess = (
  result: LicenseSearchResult,
  requestId: string,
): { type: string; result: LicenseSearchResult; requestId: string } => ({
  type: SUPER_ADMIN_SEARCH_LICENSES_SUCCESS,
  result,
  requestId,
});

export const searchLicensesFailure = (
  error: string,
  requestId: string,
): { type: string; error: string; requestId: string } => ({
  type: SUPER_ADMIN_SEARCH_LICENSES_FAILURE,
  error,
  requestId,
});

export const clearLicenseSearch = (): { type: string } => ({
  type: SUPER_ADMIN_CLEAR_LICENSE_SEARCH,
});

export type LicenseViewMode = 'search' | 'legacy';

// `tool` is the SuperAdmin tool name (TOOL_BUTTON_NAMES.INSTITUTION_LICENSE /
// .INDIVIDUAL_LICENSE) — the two license pages' view modes are tracked independently so
// switching the left-nav tool doesn't carry one page's mode over onto the other.
export const setLicenseViewMode = (
  tool: string,
  mode: LicenseViewMode,
): { type: string; tool: string; mode: LicenseViewMode } => ({
  type: SUPER_ADMIN_SET_LICENSE_VIEW_MODE,
  tool,
  mode,
});

// Live duplicate-cost-center-name check for the Create/Manage License modal — see the types
// file for why this is a dedicated slice rather than reusing elasticsearchCostCenterSearch's
// shared licenseSearch state. Used by both alternateCostCenterNameValidator (institutional-only,
// duplicate cost-center name) and validateProductAccessNotDuplicate (both license types,
// duplicate product+type on the named cost center) in license-modal/index.tsx.
export const checkCostCenterName = (
  name: string,
  costCenterType: string,
): { type: string; name: string; costCenterType: string } => ({
  type: SUPER_ADMIN_CHECK_COST_CENTER_NAME,
  name,
  costCenterType,
});

export const checkCostCenterNameSuccess = (
  name: string,
  relaxedNameMatches: string[],
  matchingLicenseRows: LicenseSelectorInfo[],
): {
  type: string;
  name: string;
  relaxedNameMatches: string[];
  matchingLicenseRows: LicenseSelectorInfo[];
} => ({
  type: SUPER_ADMIN_CHECK_COST_CENTER_NAME_SUCCESS,
  name,
  relaxedNameMatches,
  matchingLicenseRows,
});

export const checkCostCenterNameFailure = (
  name: string,
  error: string,
): { type: string; name: string; error: string } => ({
  type: SUPER_ADMIN_CHECK_COST_CENTER_NAME_FAILURE,
  name,
  error,
});

export const bulkActions = (
  actions: unknown[],
): { type: string; actions: unknown[] } => ({
  type: SUPER_ADMIN_BULK_ACTIONS,
  actions,
});

export const clearFilters = (): { type: string } => ({
  type: FILTER_ACTION_TYPES.CLEAR_FILTERS,
});

// Minimal shape of the license config object; extra fields are spread through.
interface LicenseConfig {
  institutional?: boolean;
  ownerID?: string;
  [key: string]: unknown;
}

interface CreateLicenseAction extends LicenseConfig {
  costCenterType: string;
  type: string;
  ownerID: string | undefined;
}

// Both FlowJo & SeqGeq:
export const createLicense = (config: LicenseConfig): CreateLicenseAction => ({
  ...config,
  costCenterType: config.institutional
    ? CostCenter.COST_CENTER_TYPES.institutional
    : CostCenter.COST_CENTER_TYPES.individual,
  type: SUPER_ADMIN_CREATE_LICENSE,
  ownerID: config.ownerID,
});

export const showLicenseCreationError = (
  actions: unknown[] = [],
): { type: string; actions: unknown[] } => ({
  type: SUPER_ADMIN_SHOW_CREATION_ERR,
  actions,
});

export const showLicenseCreationSuccess = (): { type: string } => ({
  type: SUPER_ADMIN_SHOW_CREATION_SUCCESS,
});

export const showLicenseManageError = (
  actions: unknown[],
): { type: string; actions: unknown[] } => ({
  type: SUPER_ADMIN_SHOW_MANAGE_ERR,
  actions,
});

export const showLicenseManageSuccess = (): { type: string } => ({
  type: SUPER_ADMIN_SHOW_MANAGE_SUCCESS,
});

export const hideLicenseModal = (): { type: string } => ({
  type: SUPER_ADMIN_HIDE_MODAL,
});

export const verifyUsersEmails = (
  users: unknown[],
): { type: string; users: unknown[] } => ({
  type: SUPER_ADMIN_VERIFY_USERS_EMAILS,
  users,
});

export const verifyUsersEmailsSuccess = (
  responses: unknown[],
): { type: string; responses: unknown[] } => ({
  type: SUPER_ADMIN_VERIFY_USERS_SUCCESS,
  responses,
});

export const requestUnPhantomUsers = (
  users: unknown[],
): { type: string; users: unknown[] } => ({
  type: SUPER_ADMIN_REQUEST_UN_PHANTOM_USERS,
  users,
});

export const unPhantomUsersSuccess = (
  responses: unknown,
): { type: string; responses: unknown } => ({
  type: SUPER_ADMIN_UN_PHANTOM_USERS_SUCCESS,
  responses,
});

export const setSelectedUserCCs = (
  costCenters: unknown[],
): { type: string; costCenters: unknown[] } => ({
  type: SUPER_ADMIN_SET_SELECTED_USER_CCS,
  costCenters,
});

// Users Records
export const setUsers = (
  users: unknown[],
): { type: string; users: unknown[] } => ({
  type: SUPER_ADMIN_SET_USERS,
  users,
});

export const addUsers = (
  users: unknown[],
): { type: string; users: unknown[] } => ({
  type: SUPER_ADMIN_ADD_USERS,
  users,
});

export const clearUsers = (): { type: string } => ({
  type: SUPER_ADMIN_CLEAR_USERS,
});

export const searchComplete = (
  requestId: string,
  error: string | null = null,
): { type: string; requestId: string; error: string | null } => ({
  type: SUPER_ADMIN_SEARCH_COMPLETE,
  requestId,
  error,
});

// Product Versions
export const fetchProductVersions = (): { type: string } => ({
  type: SUPER_ADMIN_FETCH_PRODUCT_VERSIONS,
});

export const productVersionsFetchSuccess = (
  products: unknown,
): { type: string; products: unknown } => ({
  type: SUPER_ADMIN_PRODUCT_VERSIONS_FETCH_SUCCESS,
  products,
});

export const updateProductWhitelist = (
  productId: string,
  addCountries: string[],
  removeCountries: string[],
): {
  type: string;
  productId: string;
  addCountries: string[];
  removeCountries: string[];
} => ({
  type: SUPER_ADMIN_UPDATE_PRODUCT_WHITELIST,
  productId,
  addCountries,
  removeCountries,
});

export const updateProductWhitelistSuccess = (): { type: string } => ({
  type: SUPER_ADMIN_UPDATE_PRODUCT_WHITELIST_SUCCESS,
});

export const createProductVersion = (
  name: string,
  productVersion: string,
  region: string,
  productType: string,
  releaseDate: string,
  whitelistedCountries: string[],
  active: boolean,
): {
  type: string;
  name: string;
  productVersion: string;
  region: string;
  productType: string;
  releaseDate: string;
  whitelistedCountries: string[];
  active: boolean;
} => ({
  type: SUPER_ADMIN_CREATE_PRODUCT_VERSION,
  name,
  productVersion,
  region,
  productType,
  releaseDate,
  whitelistedCountries,
  active,
});

export const createProductVersionSuccess = (
  product: unknown,
): { type: string; product: unknown } => ({
  type: SUPER_ADMIN_CREATE_PRODUCT_VERSION_SUCCESS,
  product,
});

export const updateProductAssets = (
  productId: string,
  assets: unknown,
): { type: string; productId: string; assets: unknown } => ({
  type: SUPER_ADMIN_UPDATE_PRODUCT_ASSETS,
  productId,
  assets,
});

export const updateProductAssetsSuccess = (
  product: unknown,
): { type: string; product: unknown } => ({
  type: SUPER_ADMIN_UPDATE_PRODUCT_ASSETS_SUCCESS,
  product,
});

export const updateProductActive = (
  productId: string,
  active: boolean,
): { type: string; productId: string; active: boolean } => ({
  type: SUPER_ADMIN_UPDATE_PRODUCT_IS_ACTIVE,
  productId,
  active,
});

export const updateProductActiveSuccess = (
  product: unknown,
): { type: string; product: unknown } => ({
  type: SUPER_ADMIN_UPDATE_PRODUCT_IS_ACTIVE_SUCCESS,
  product,
});

// Product Features
export const fetchProductFeatures = (
  productName: string,
): { type: string; productName: string } => ({
  type: SUPER_ADMIN_FETCH_PRODUCT_FEATURES,
  productName,
});

/**
 * Per-seat feature overrides for one product access. Keyed by cost centre + product access
 * because that pair is what the backend read is scoped to.
 */
export const fetchSeatFeatures = (
  costCenterId: string,
  productAccessId: string,
): { type: string; costCenterId: string; productAccessId: string } => ({
  type: SUPER_ADMIN_FETCH_SEAT_FEATURES,
  costCenterId,
  productAccessId,
});

export const seatFeaturesFetchSuccess = (
  costCenterId: string,
  productAccessId: string,
  features: unknown,
): { type: string; costCenterId: string; productAccessId: string; features: unknown } => ({
  type: SUPER_ADMIN_SEAT_FEATURES_FETCH_SUCCESS,
  costCenterId,
  productAccessId,
  features,
});

/**
 * Applies one feature to every selected seat. `enabled: null` clears the override, returning
 * those seats to inheriting the product-access level value - a different state from `false`.
 */
export const setSeatFeature = (args: {
  costCenterId: string;
  productAccessId: string;
  userIds: string[];
  featureName: string;
  value: boolean | string | number;
  enabled: boolean | null;
}): { type: string } & typeof args => ({
  type: SUPER_ADMIN_SET_SEAT_FEATURE,
  ...args,
});

export const productFeaturesFetchSuccess = (
  productName: string,
  features: unknown,
): { type: string; productName: string; features: unknown } => ({
  type: SUPER_ADMIN_PRODUCT_FEATURES_FETCH_SUCCESS,
  productName,
  features,
});

// User Field Updates
export const updateUserField = (
  userEmail: string,
  fieldKey: string,
  value: unknown,
): { type: string; userEmail: string; fieldKey: string; value: unknown } => ({
  type: SUPER_ADMIN_UPDATE_USER_FIELD,
  userEmail,
  fieldKey,
  value,
});

export const updateUserFieldSuccess = (
  user: unknown,
): { type: string; user: unknown } => ({
  type: SUPER_ADMIN_UPDATE_USER_FIELD_SUCCESS,
  user,
});

export const updateUserFieldError = (
  message: string,
): { type: string; message: string } => ({
  type: SUPER_ADMIN_UPDATE_USER_FIELD_ERROR,
  message,
});

export const setUserPhantom = (
  userId: string,
  isPhantom: boolean,
): { type: string; userId: string; isPhantom: boolean } => ({
  type: SUPER_ADMIN_SET_USER_PHANTOM,
  userId,
  isPhantom,
});

export const setUserPhantomSuccess = (
  user: unknown,
): { type: string; user: unknown } => ({
  type: SUPER_ADMIN_SET_USER_PHANTOM_SUCCESS,
  user,
});

export const setUserEmailVerified = (
  email: string,
  verified: boolean,
): { type: string; email: string; verified: boolean } => ({
  type: SUPER_ADMIN_SET_USER_EMAIL_VERIFIED,
  email,
  verified,
});

export const setUserEmailVerifiedSuccess = (
  user: unknown,
): { type: string; user: unknown } => ({
  type: SUPER_ADMIN_SET_USER_EMAIL_VERIFIED_SUCCESS,
  user,
});

export const setUserEmailVerifiedError = (
  message: string,
): { type: string; message: string } => ({
  type: SUPER_ADMIN_SET_USER_EMAIL_VERIFIED_ERROR,
  message,
});

export const setUserPhantomError = (
  message: string,
): { type: string; message: string } => ({
  type: SUPER_ADMIN_SET_USER_PHANTOM_ERROR,
  message,
});

// TODO: password reset is deferred until a restricted admin role is available

// Archive Active EOT
export const fetchActiveEot = (
  userId: string,
  productAccessId: string,
): { type: string; userId: string; productAccessId: string } => ({
  type: SUPER_ADMIN_FETCH_ACTIVE_EOT,
  userId,
  productAccessId,
});

export const fetchActiveEotSuccess = (
  eot: unknown,
): { type: string; eot: unknown } => ({
  type: SUPER_ADMIN_FETCH_ACTIVE_EOT_SUCCESS,
  eot,
});

export const fetchActiveEotError = (
  message: string,
): { type: string; message: string } => ({
  type: SUPER_ADMIN_FETCH_ACTIVE_EOT_ERROR,
  message,
});

export const archiveActiveEot = (
  eotId: string,
): { type: string; eotId: string } => ({
  type: SUPER_ADMIN_ARCHIVE_ACTIVE_EOT,
  eotId,
});

export const archiveActiveEotSuccess = (): { type: string } => ({
  type: SUPER_ADMIN_ARCHIVE_ACTIVE_EOT_SUCCESS,
});

export const archiveActiveEotError = (
  message: string,
): { type: string; message: string } => ({
  type: SUPER_ADMIN_ARCHIVE_ACTIVE_EOT_ERROR,
  message,
});

export const archiveActiveEotReset = (): { type: string } => ({
  type: SUPER_ADMIN_ARCHIVE_ACTIVE_EOT_RESET,
});
